// Huffman Node class
class HuffmanNode {
  constructor(char, freq) {
    this.char = char;
    this.freq = freq;
    this.left = null;
    this.right = null;
  }
}

// Function to build frequency map
function buildFrequencyMap(data) {
  const freqMap = new Map();
  for (const byte of data) {
    freqMap.set(byte, (freqMap.get(byte) || 0) + 1);
  }
  return freqMap;
}

// Function to build Huffman tree
function buildHuffmanTree(freqMap) {
  const nodes = [];
  
  // Create leaf nodes for each character with frequency
  for (const [char, freq] of freqMap.entries()) {
    nodes.push(new HuffmanNode(char, freq));
  }
  
  // Build the Huffman tree
  while (nodes.length > 1) {
    // Sort nodes by frequency (ascending)
    nodes.sort((a, b) => a.freq - b.freq);
    
    // Take the two nodes with the lowest frequency
    const left = nodes.shift();
    const right = nodes.shift();
    
    // Create a new internal node with these two nodes as children
    const internalNode = new HuffmanNode(null, left.freq + right.freq);
    internalNode.left = left;
    internalNode.right = right;
    
    // Add the new node to the nodes array
    nodes.push(internalNode);
  }
  
  return nodes[0]; // The root of the Huffman tree
}

// Function to build Huffman codes
function buildHuffmanCodes(node, prefix = '', codeMap = new Map()) {
  if (node.char !== null) {
    // Leaf node
    codeMap.set(node.char, prefix);
  } else {
    // Internal node
    buildHuffmanCodes(node.left, prefix + '0', codeMap);
    buildHuffmanCodes(node.right, prefix + '1', codeMap);
  }
  return codeMap;
}

// Function to serialize the Huffman tree for decompression
function serializeHuffmanTree(node) {
  if (node === null) return null;
  
  const serialized = {
    char: node.char,
    freq: node.freq,
    left: serializeHuffmanTree(node.left),
    right: serializeHuffmanTree(node.right)
  };
  
  return serialized;
}

// Function to deserialize the Huffman tree
function deserializeHuffmanTree(serialized) {
  if (serialized === null) return null;
  
  const node = new HuffmanNode(serialized.char, serialized.freq);
  node.left = deserializeHuffmanTree(serialized.left);
  node.right = deserializeHuffmanTree(serialized.right);
  
  return node;
}

// Main encoding function
export function huffmanEncode(data) {
  if (data.length === 0) return { encoded: new Uint8Array(0), tree: null };
  
  // Build frequency map
  const freqMap = buildFrequencyMap(data);
  
  // Build Huffman tree
  const huffmanTree = buildHuffmanTree(freqMap);
  
  // Build Huffman codes
  const codeMap = buildHuffmanCodes(huffmanTree);
  
  // Encode the data
  let encodedBits = '';
  for (const byte of data) {
    encodedBits += codeMap.get(byte);
  }
  
  // Convert bit string to bytes
  const encodedBytes = new Uint8Array(Math.ceil(encodedBits.length / 8));
  let byteIndex = 0;
  let bitIndex = 0;
  
  for (let i = 0; i < encodedBits.length; i++) {
    if (encodedBits[i] === '1') {
      encodedBytes[byteIndex] |= (1 << (7 - bitIndex));
    }
    
    bitIndex++;
    if (bitIndex === 8) {
      byteIndex++;
      bitIndex = 0;
    }
  }
  
  // Store the number of padding bits in the last byte
  const paddingBits = (8 - (encodedBits.length % 8)) % 8;
  
  return {
    encoded: encodedBytes,
    tree: serializeHuffmanTree(huffmanTree),
    originalLength: data.length,
    paddingBits
  };
}

// Main decoding function
export function huffmanDecode(encodedData, serializedTree, originalLength, paddingBits = 0) {
  if (encodedData.length === 0) return new Uint8Array(0);
  
  // Deserialize the Huffman tree
  const huffmanTree = deserializeHuffmanTree(serializedTree);
  
  // Convert encoded bytes to bit string
  let bitString = '';
  for (let i = 0; i < encodedData.length; i++) {
    const byte = encodedData[i];
    // Skip padding bits in the last byte
    const bits = (i === encodedData.length - 1) 
      ? (byte >>> 0).toString(2).padStart(8 - paddingBits, '0')
      : (byte >>> 0).toString(2).padStart(8, '0');
    bitString += bits;
  }
  
  // Decode the bit string
  const decodedData = new Uint8Array(originalLength);
  let decodedIndex = 0;
  let currentNode = huffmanTree;
  
  for (const bit of bitString) {
    if (bit === '0') {
      currentNode = currentNode.left;
    } else {
      currentNode = currentNode.right;
    }
    
    // If we've reached a leaf node
    if (currentNode.char !== null) {
      decodedData[decodedIndex++] = currentNode.char;
      if (decodedIndex >= originalLength) break; // Stop if we've decoded all original bytes
      currentNode = huffmanTree; // Reset to the root for the next character
    }
  }
  
  return decodedData;
}
